/**
 * The webRecorder2's interface to the browser's MediaRecorder API.
 *
 * Note that this does not follow the MVUReducer pattern of State/Action/SideEffect,
 * because literally *any* interaction with the MediaRecorder is a side effect.
 * Thus, any operations on the Recorder can only be called from the webRecorder2's
 * side effect executor.
 */

import debounce from 'lodash.debounce'
import { drop } from '~/utils/map'
import { assert } from '~/utils/assert'
import * as RecordingStorage from './recordingStorage'
import { defaultMimeType } from '~/utils/mime'
import { sleep } from '~/utils/sleep'
import * as PubSub from './pubSub'
import { Toast } from '~/toasts/toasts'
import * as GlobalPubSub from '~/pubSub'
import { withoutWhitespace } from '~/utils/string'
import { totalBytes } from '~/utils/blob'

// This mirrors :audio_state in the backend's RecordAudio component.
export type AudioState = 'recording' | 'stopped'

type AudioGraph = {
  context: AudioContext
  destination: MediaStreamAudioDestinationNode
  currentSource: MediaStreamAudioSourceNode
  analyser: AnalyserNode
  filter: BiquadFilterNode
}

type InitializedState = {
  recorder: MediaRecorder
  storage: RecordingStorage.State
  audioGraph: AudioGraph
  prevDurationMs: number
}

type Interval = ReturnType<typeof setInterval>

export type ActiveState = InitializedState & {
  startTimeMs: number
  interval: Interval
}

export type PausedState = InitializedState

type StartRecordingError = { error: string }

type TrackAnalytics = (message: string) => void
type BroadcastRecordingState = (state: AudioState) => void

// Used for sanity checking that we're not accidentally trying to
// start multiple recordings at once, leading to garbled, doubled up recordings.
let sanityCheckRecorder: MediaRecorder | null = null

// Used for communication between different tabs/windows on our domain.
// This lets us know when another tab is recording, so we can prevent
// two tabs writing to the same local database at the same time.
const _ipcChannel = new BroadcastChannel('recorder-channel')
// The storage writerID that's currently recording
let _ipcRecordingId: string | null = null

// The interval at which we broadcast the local recording state to other tabs/windows.
const localRecordingStateBroadcastIntervalMs = 500

function subscribeToRecordingClientId(setRecordingClientId: (recordingClientId: string | null) => void) {
  const listener = (event: MessageEvent) => {
    if (event.data.type === 'recording-state') {
      if (event.data.writerId === _ipcRecordingId && event.data.state === 'stopped') {
        _ipcRecordingId = null
      } else if (event.data.state === 'recording') {
        _ipcRecordingId = event.data.writerId
      }

      setRecordingClientId(_ipcRecordingId)
    }
  }

  _ipcChannel.addEventListener('message', listener)

  return () => {
    _ipcChannel.removeEventListener('message', listener)
  }
}

subscribeToRecordingClientId((recordingClientId) => {
  _ipcRecordingId = recordingClientId

  PubSub.publish('apply-action', { action: 'set-recording-client-id', clientId: recordingClientId })
})

// When this promise has resolved, we can be confident that we've waited long enough
// that if another tab is recording, it will have broadcast its state to _ipcRecordingId,
//  so we can ensure we aren't starting a second local recording.
//
// Note that Chrome seems to publish BroadcastChannel messages in 1 second
// intervals at most.
export const otherClientRecordingStateKnown = sleep(1_000)

export function isOtherClientRecording(storage: RecordingStorage.State): boolean {
  return _ipcRecordingId !== null && _ipcRecordingId !== storage.writerId
}

export function startRecording(
  storage: RecordingStorage.State,
  stream: MediaStream,
  trackAnalytics: TrackAnalytics,
  broadcastRecordingState: BroadcastRecordingState
): ActiveState | StartRecordingError {
  return initializeRecording(storage, stream, trackAnalytics, broadcastRecordingState, { startPaused: false }) as
    | ActiveState
    | StartRecordingError
}

export function initializeRecording(
  storage: RecordingStorage.State,
  stream: MediaStream,
  trackAnalytics: TrackAnalytics,
  broadcastRecordingState: BroadcastRecordingState,
  { startPaused }: { startPaused: boolean }
): ActiveState | PausedState | StartRecordingError {
  assert(sanityCheckRecorder === null, 'recorder is already initialized')

  if (isOtherClientRecording(storage)) {
    return { error: 'Another tab in your browser is capturing a meeting' }
  }

  const prevDurationMs = RecordingStorage.getDurationMs(storage)
  debugStream(`Initializing recording; previous duration was ${prevDurationMs / 1_000} seconds;`, stream)

  if (prevDurationMs === 0 || !RecordingStorage.getStartTimeMs(storage)) {
    RecordingStorage.setStartTimeMs(storage, Date.now())
  }

  const audioTracks = stream.getAudioTracks()
  if (audioTracks.length === 0) {
    return { error: 'No audio tracks found in stream' }
  }

  const audioTrack = audioTracks[0]
  debugLog('Audio track settings:', audioTrack.getSettings())

  const mimeType = defaultMimeType()

  if (!mimeType) {
    return { error: 'No supported MIME type found for MediaRecorder.' }
  }

  const context = new AudioContext()
  const audioDestination = context.createMediaStreamDestination()
  const source = context.createMediaStreamSource(stream)
  const analyser = context.createAnalyser()
  analyser.fftSize = 128
  analyser.smoothingTimeConstant = 0.8
  analyser.minDecibels = -85
  analyser.maxDecibels = -20
  const filter = context.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.value = 1800
  filter.Q.value = 0.5

  source.connect(filter)
  filter.connect(analyser)
  analyser.connect(audioDestination)
  const audioGraph = { context, destination: audioDestination, currentSource: source, analyser, filter }

  const recorder = new MediaRecorder(audioDestination.stream, {
    mimeType: mimeType,
    bitsPerSecond: 16000,
    audioBitsPerSecond: 16000,
  })

  sanityCheckRecorder = recorder

  assert(recorder.mimeType === mimeType, `Expected mimeType to be ${mimeType} but got ${recorder.mimeType}`)
  RecordingStorage.setMimeType(storage, mimeType)

  debugLog('MediaRecorder configuration:', {
    mimeType: recorder.mimeType,
    state: recorder.state,
    audioBitsPerSecond: recorder.audioBitsPerSecond,
  })

  context.onstatechange = () => {
    if (['suspended', 'closed'].includes(context.state) && recorder.state === 'recording') {
      debugError('While recording, audio context state changed to', context.state)
    }
  }

  stream.addEventListener('inactive', () => {
    if (recorder.state === 'recording') {
      debugError('While recording, audio input stream became inactive')
    }
  })

  stream.addEventListener('removetrack', () => {
    if (recorder.state === 'recording') {
      debugError('While recording, audio input stream had a track removed')
    }
  })

  for (const track of stream.getTracks()) {
    track.addEventListener('ended', () => {
      if (recorder.state === 'recording') {
        debugError('While recording, audio input stream had a track end')
      }
    })
  }

  if (startPaused) {
    RecordingStorage.setIsRecording(storage, false)
    return { storage, recorder, audioGraph, prevDurationMs }
  } else {
    const startTimeMs = Date.now()
    debugLog('Started at', new Date(startTimeMs).toLocaleTimeString())

    const wakeLockPromise = navigator.wakeLock?.request('screen').catch((err) => {
      debugWarn('Failed to prevent the computer from sleeping:', err)
    })

    const interval = setInterval(() => {
      broadcastRecordingStateLocal(storage, recorder.state === 'recording' ? 'recording' : 'stopped')

      // Tyler notes: This shouldn't be possible, but I'm loathe to remove someone else's defensive code.
      if (recorder.state !== 'recording') {
        debugLog('recording interval was running when recorder was not recording')
        stopPreviousRecording({ recorder, interval, audioGraph })
      }
    }, localRecordingStateBroadcastIntervalMs)

    recorder.ondataavailable = async (event: BlobEvent) => {
      if (recorder.mimeType && withoutWhitespace(recorder.mimeType) !== withoutWhitespace(mimeType)) {
        debugWarn('Mime type changed from', mimeType, 'to', recorder.mimeType)
        RecordingStorage.setMimeType(storage, recorder.mimeType)
      }

      if (event.data && event.data.size > 0) {
        const currentSessionDurationMs = Date.now() - startTimeMs

        try {
          await RecordingStorage.addChunk(storage, event.data)
        } catch (error) {
          debugError('Failed to save chunk', error)

          const toast: Toast = {
            content: 'Failed to save an audio sample. Your browser may not support audio capture.',
            type: 'error',
          }

          GlobalPubSub.publish('show-toast', toast)
        }

        const durationMs = prevDurationMs + currentSessionDurationMs
        RecordingStorage.setDurationMs(storage, durationMs)

        // Tyler says: I'd like the first 5 seconds of every recording to be logged noisily
        // in Posthog, but subsequent updates need to be *way* less frequent, because Posthog
        // has a hard upper limit for how many console events they'll store for a given
        // page view.
        const durationSec = durationMs / 1_000

        const logDuration = debounce((sec: number) => {
          debugLog(`Total recording duration: ${sec} seconds`)
        }, 1000)

        if (currentSessionDurationMs < 5_000 || Math.round(durationSec) % 10 === 0) {
          logDuration(durationSec)
        }

        // We'll get one very last ondataavailable event when the recording stops.
        broadcastRecordingState(recorder.state === 'recording' ? 'recording' : 'stopped')
      } else {
        debugWarn('No data available in ondataavailable event', event.data)
      }
    }

    recorder.onstop = async () => {
      // We don't want to log spurious warnings when the MIME type changes from
      // "audio/webm;codecs=opus" to
      // "audio/webm; codecs=opus"
      // (which is a real thing I've seen in Safari on macOS)
      if (recorder.mimeType && withoutWhitespace(recorder.mimeType) !== withoutWhitespace(mimeType)) {
        debugWarn('Mime type changed from', mimeType, 'to', recorder.mimeType)
      }

      debugLog('Stopping recorder')
      broadcastRecordingState('stopped')
      broadcastRecordingStateLocal(storage, 'stopped')

      stream.getTracks().forEach((track) => track.stop())
      const audioChunks = await RecordingStorage.getAllChunks(storage)
      const totalSize = totalBytes(audioChunks)
      const durationMs = RecordingStorage.getDurationMs(storage)

      debugLog(
        `Stopped recording having ${audioChunks.length} audio chunks totaling ${totalSize} bytes,`,
        `with duration ${durationMs / 1_000} seconds`,
        audioChunks,
        new Blob(audioChunks, { type: mimeType })
      )

      wakeLockPromise?.then((wakeLock) => {
        debugLog('Allowing the computer to sleep again')
        wakeLock?.release()
      })
    }

    window.addEventListener('beforeunload', () => broadcastRecordingStateLocal(storage, 'stopped'))

    recorder.start(500)
    RecordingStorage.setIsRecording(storage, true)
    trackAnalytics('Started web recording')

    return { storage, recorder, audioGraph, startTimeMs, interval, prevDurationMs }
  }
}

export function switchInputStream<T extends { audioGraph: AudioGraph }>(state: T, stream: MediaStream): T {
  debugStream('Switching input', stream)

  const { audioGraph } = state
  audioGraph.currentSource.disconnect()

  const source = audioGraph.context.createMediaStreamSource(stream)
  source.connect(audioGraph.filter)
  audioGraph.filter.connect(audioGraph.analyser)
  audioGraph.analyser.connect(audioGraph.destination)

  return { ...state, audioGraph: { ...audioGraph, currentSource: source } }
}

export function stopPreviousRecording({
  recorder,
  interval,
  audioGraph,
}: {
  recorder?: MediaRecorder
  interval?: Interval
  audioGraph?: AudioGraph
}) {
  if (recorder?.state === 'recording') {
    debugLog('Stopping previous recording')

    recorder.stop()
    recorder.stream.getTracks().forEach((track) => track.stop())
  }

  if (interval) {
    clearInterval(interval)
  }

  if (audioGraph?.currentSource) {
    audioGraph.currentSource.disconnect()
    audioGraph.context.close().catch((error) => {
      debugWarn('[Audio Recorder] Error closing audio context', error)
    })
  }

  if (recorder) {
    assertWhenNotTesting(
      sanityCheckRecorder === recorder || sanityCheckRecorder === null,
      'recorder is not the same as the one being stopped'
    )

    sanityCheckRecorder = null
  } else if (sanityCheckRecorder) {
    debugError('had an active recorder, but no record of it; stopping it anyways')

    if (sanityCheckRecorder.state === 'recording') {
      sanityCheckRecorder.stop()
      sanityCheckRecorder.stream.getTracks().forEach((track) => track.stop())
    }

    sanityCheckRecorder = null
  }
}

export function pauseRecording(state: ActiveState, nowUnixMs: number = Date.now()): PausedState {
  const { recorder, interval, startTimeMs, storage, prevDurationMs: prevRecordedMs } = state

  clearInterval(interval)

  if (recorder.state === 'recording') {
    assert(startTimeMs, 'Expected startTimeMs to be set')
    const recordedMs = startTimeMs ? nowUnixMs - startTimeMs : 0
    const totalRecordedMs = prevRecordedMs + recordedMs
    RecordingStorage.setDurationMs(storage, totalRecordedMs)

    assertWhenNotTesting(sanityCheckRecorder === recorder, 'recorder is not the same as the one being paused')

    // TODO: We could be using recorder.pause() and not have to reboot the recorder every time we unpause
    recorder.stop()
    RecordingStorage.setIsRecording(storage, false)

    debugLog(
      `Paused recording at ${new Date(nowUnixMs).toLocaleTimeString()} with ${totalRecordedMs / 1_000} seconds recorded in total,`,
      `of which ${recordedMs / 1_000} seconds were recorded since the last pause.`
    )

    return { ...drop(state, ['startTimeMs', 'interval']), prevDurationMs: totalRecordedMs }
  }
  return state
}

export function resumeRecording(
  state: PausedState,
  stream: MediaStream,
  trackAnalytics: TrackAnalytics,
  broadcastRecordingState: BroadcastRecordingState
): ActiveState | StartRecordingError {
  return startRecording(state.storage, stream, trackAnalytics, broadcastRecordingState)
}

export function recordingDurationSeconds(
  { prevDurationMs, startTimeMs }: { prevDurationMs: number; startTimeMs?: number },
  nowUnixMs: number = Date.now()
): number {
  const inProgressDurationMs = startTimeMs ? nowUnixMs - startTimeMs : 0
  return (prevDurationMs + inProgressDurationMs) / 1_000
}

export function receivingAudioData({ audioGraph: { analyser } }: ActiveState): boolean {
  const dataArray = new Uint8Array(analyser.frequencyBinCount)
  analyser.getByteFrequencyData(dataArray)
  return dataArray.some((value) => value > 0)
}

declare global {
  // Voice isolation is non-standard as of 2024, but it's supported in some browsers,
  // and it's certainly useful where available.
  interface MediaTrackConstraintSet {
    voiceIsolation?: boolean
  }
}

export async function initializeStream(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: deviceId ?? 'default',
      channelCount: 1,
      sampleRate: 16000,
      sampleSize: 16,
      voiceIsolation: true,
    },
  })
}

export function mimeType({ recorder }: InitializedState): string {
  return recorder.mimeType
}

// We use this for assertions we'll violate in unit tests (because we can't actually
// initialize a real recording).
function assertWhenNotTesting(condition: boolean, message: string) {
  if (process.env.NODE_ENV !== 'test') {
    assert(condition, message)
  }
}

// Exported so that in testing, we can mock it to a no-op
export function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log(`[Audio Recorder]`, ...args)
}

function debugWarn(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.warn(`[Audio Recorder]`, ...args)
}

function debugError(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.error(`[Audio Recorder]`, ...args)
}

function broadcastRecordingStateLocal(storage: RecordingStorage.State, state: AudioState) {
  _ipcChannel.postMessage({ type: 'recording-state', state, writerId: storage.writerId })
}

function debugStream(prefix: string, stream: MediaStream) {
  debugLog(
    prefix,
    'stream',
    stream.id,
    stream.active ? 'active' : 'inactive',
    'with tracks: ',
    stream.getTracks().map((t) => t.id)
  )
}
